import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Edit2, Trash2, Save, Award, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export default function BenefitsPage() {
  const { token } = useAuth();
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [formData, setFormData] = useState({
    name_en: "",
    name_de: "",
    description_en: "",
    description_de: "",
    info_video_url: "",
    category: "general",
    icon: "check",
    included_in: ["A", "B", "C", "D"],
    highlight: false
  });

  useEffect(() => {
    fetchBenefits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchBenefits = async () => {
    try {
      const res = await axios.get(`${API}/benefits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBenefits(res.data);
    } catch (error) {
      toast.error("Failed to load benefits");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBenefit) {
        await axios.put(`${API}/benefits/${editingBenefit.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/benefits`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Created");
      }
      setDialogOpen(false);
      setEditingBenefit(null);
      resetForm();
      fetchBenefits();
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  const handleEdit = (benefit) => {
    setEditingBenefit(benefit);
    setFormData({
      name_en: benefit.name_en,
      name_de: benefit.name_de,
      description_en: benefit.description_en,
      description_de: benefit.description_de,
      info_video_url: benefit.info_video_url || "",
      category: benefit.category,
      icon: benefit.icon,
      included_in: benefit.included_in,
      highlight: benefit.highlight
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this benefit?")) return;
    try {
      await axios.delete(`${API}/benefits/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted");
      fetchBenefits();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setFormData({
      name_en: "",
      name_de: "",
      description_en: "",
      description_de: "",
      info_video_url: "",
      category: "general",
      icon: "check",
      included_in: ["A", "B", "C", "D"],
      highlight: false
    });
  };

  const togglePlan = (plan) => {
    setFormData(prev => ({
      ...prev,
      included_in: prev.included_in.includes(plan)
        ? prev.included_in.filter(p => p !== plan)
        : [...prev.included_in, plan]
    }));
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl text-white mb-1" data-testid="benefits-title">Benefits</h1>
          <p className="text-white/50">Features included in each plan</p>
        </div>
        <button
          onClick={() => { setEditingBenefit(null); resetForm(); setDialogOpen(true); }}
          className="btn-accent flex items-center gap-2"
          data-testid="create-benefit-btn"
        >
          <Plus className="w-5 h-5" />
          New Benefit
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner" />
        </div>
      ) : benefits.length === 0 ? (
        <div className="glass-card-static p-12 text-center">
          <Award className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <h3 className="font-display text-xl text-white mb-2">No benefits yet</h3>
          <p className="text-white/40 mb-6">Create benefits to show in proposals</p>
          <button
            onClick={() => setDialogOpen(true)}
            className="btn-accent inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Benefit
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`glass-card p-6 ${benefit.highlight ? 'popular' : ''}`}
              data-testid={`benefit-card-${benefit.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    benefit.highlight ? "bg-[#C9A96E]/20" : "bg-white/5"
                  }`}>
                    <Award className={`w-5 h-5 ${benefit.highlight ? "text-[#C9A96E]" : "text-white/60"}`} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">{benefit.name_en}</h3>
                    <p className="text-white/40 text-sm">{benefit.name_de}</p>
                  </div>
                </div>
                {benefit.highlight && (
                  <Star className="w-5 h-5 text-[#C9A96E] fill-[#C9A96E]" />
                )}
              </div>
              
              <p className="text-white/50 text-sm mb-4 line-clamp-2">{benefit.description_en}</p>
              
              <div className="flex items-center justify-between mb-4">
                <span className="text-white/30 text-xs uppercase tracking-wider">{benefit.category}</span>
                <div className="flex gap-1">
                  {["A", "B", "C", "D"].map(plan => (
                    <span 
                      key={plan} 
                      className={`w-6 h-6 rounded-full text-xs flex items-center justify-center ${
                        benefit.included_in.includes(plan)
                          ? "bg-[#C9A96E]/20 text-[#C9A96E]"
                          : "bg-white/5 text-white/20"
                      }`}
                    >
                      {plan}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(benefit)}
                  className="flex-1 btn-secondary py-2 text-sm flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(benefit.id)}
                  className="px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A0A0A]/95 backdrop-blur-xl border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">
              {editingBenefit ? "Edit Benefit" : "New Benefit"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group mb-0">
                <label className="form-label">Name (EN) *</label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group mb-0">
                <label className="form-label">Name (DE)</label>
                <input
                  type="text"
                  value={formData.name_de}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_de: e.target.value }))}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group mb-0">
              <label className="form-label">Description (EN)</label>
              <textarea
                value={formData.description_en}
                onChange={(e) => setFormData(prev => ({ ...prev, description_en: e.target.value }))}
                className="form-input min-h-[80px]"
                placeholder="Shown as info tooltip on public page..."
              />
            </div>

            <div className="form-group mb-0">
              <label className="form-label">Info Video URL</label>
              <input
                type="url"
                value={formData.info_video_url}
                onChange={(e) => setFormData(prev => ({ ...prev, info_video_url: e.target.value }))}
                className="form-input"
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group mb-0">
                <label className="form-label">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="form-input"
                >
                  <option value="general">General</option>
                  <option value="visibility">Visibility</option>
                  <option value="support">Support</option>
                  <option value="marketing">Marketing</option>
                </select>
              </div>
              <div className="form-group mb-0">
                <label className="form-label">Highlight</label>
                <label className="flex items-center gap-3 cursor-pointer h-[46px]">
                  <input
                    type="checkbox"
                    checked={formData.highlight}
                    onChange={(e) => setFormData(prev => ({ ...prev, highlight: e.target.checked }))}
                    className="w-5 h-5 rounded border-white/20 bg-transparent text-[#C9A96E] focus:ring-[#C9A96E]"
                  />
                  <span className="text-white/60 text-sm">Featured benefit</span>
                </label>
              </div>
            </div>

            <div className="form-group mb-0">
              <label className="form-label">Included in Plans</label>
              <div className="flex gap-2">
                {["A", "B", "C", "D"].map(plan => (
                  <button
                    key={plan}
                    type="button"
                    onClick={() => togglePlan(plan)}
                    className={`w-12 h-12 rounded-xl font-semibold transition-colors ${
                      formData.included_in.includes(plan)
                        ? "bg-[#C9A96E] text-white"
                        : "bg-white/5 text-white/40 hover:bg-white/10"
                    }`}
                  >
                    {plan}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setDialogOpen(false)} className="flex-1 btn-secondary">
                Cancel
              </button>
              <button type="submit" className="flex-1 btn-accent flex items-center justify-center gap-2">
                <Save className="w-4 h-4" />
                {editingBenefit ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
